import {
  BULK_APPROVE_MAX_CONTAINERS,
  ContainerKind,
  DSAttendanceStatus
} from '@/constants/ds-attendance.constants'
import { toDDMMYYYY } from './activity-time'

/**
 * F2 (M3-2) — motivo único pelo qual um Registro de Atividade nunca entra no lote. Fica
 * aqui, junto da regra, para o botão e o tooltip da fila contarem a mesma história.
 */
export const ACTIVITY_NOT_BULK_APPROVABLE_REASON =
  'Registro de Atividade é auditado um a um: não entra na aprovação em lote.'

/**
 * Apontamento 4 (vídeo 03/09) — o endpoint de aprovação em lote existe desde a fase 28 e
 * nunca teve botão. Aprovar em lote alimenta PAGAMENTO, então a elegibilidade é decidida
 * aqui, em função pura e testável, e o default é sempre FECHADO: sem a flag da policy no
 * payload da listagem, nenhum container é elegível.
 */

/** O mínimo que a listagem precisa expor para o lote ser decidido. */
export interface BulkApproveCandidate {
  attendanceId: number
  status: DSAttendanceStatus
  /** `true` = alguma linha divergente, ou seja NÃO está 100% conciliado. */
  hasDivergentEntries?: boolean
  /** Vem da policy do container. Ausente = tratado como desligado. */
  bulkApproveEnabled?: boolean
  /**
   * F2 (M3-2) — espécie do container. Ausente = backend anterior à F2 = `FOLHA`, então
   * a elegibilidade de hoje não muda em nada. `ATIVIDADE` é auditado um a um.
   */
  kind?: ContainerKind | string | null
}

/** Aprovação em lote só faz sentido no que ainda está em auditoria. */
const APPROVABLE_STATUSES: DSAttendanceStatus[] = [
  DSAttendanceStatus.PENDING_AUDIT,
  DSAttendanceStatus.AUDIT_IN_PROGRESS
]

/** F2 (M3-2) — `ATIVIDADE` é auditado um a um; o backend devolve `ACTIVITY_NOT_BULK_APPROVABLE`. */
export function isActivityContainer(candidate: Pick<BulkApproveCandidate, 'kind'>): boolean {
  return candidate.kind === ContainerKind.ATIVIDADE
}

export function isBulkApproveEligible(candidate: BulkApproveCandidate): boolean {
  return (
    candidate.bulkApproveEnabled === true &&
    candidate.hasDivergentEntries !== true &&
    !isActivityContainer(candidate) &&
    APPROVABLE_STATUSES.includes(candidate.status)
  )
}

/**
 * Os ids que vão no payload, já cortados no teto de 50 do backend (`@ArrayMaxSize(50)`) —
 * mandar mais devolveria 400 e nenhum container seria aprovado.
 */
export function selectBulkApproveIds(candidates: BulkApproveCandidate[]): number[] {
  return candidates
    .filter(isBulkApproveEligible)
    .map((candidate) => candidate.attendanceId)
    .slice(0, BULK_APPROVE_MAX_CONTAINERS)
}

/** Resumo do resultado do lote para o toast — nem só sucesso, nem só falha. */
export function bulkApproveResultMessage(approved: number[], failed: unknown[]): string {
  const approvedLabel =
    approved.length === 1 ? '1 registro aprovado' : `${approved.length} registros aprovados`
  if (failed.length === 0) return `${approvedLabel} em lote.`
  const failedLabel =
    failed.length === 1 ? '1 não pôde ser aprovado' : `${failed.length} não puderam ser aprovados`
  return `${approvedLabel}; ${failedLabel}.`
}

/** Spec 2026-09-24 §6.4 (Q12): o plantão já tem outro registro aprovado na mesma perna. */
export const REQUIRES_OVERWRITE_CONFIRMATION = 'REQUIRES_OVERWRITE_CONFIRMATION'

/**
 * Rótulo por motivo do lote. Motivo fora da tabela passa como veio: o `failed` do backend
 * carrega a mensagem pt-BR da exceção quando a aprovação falha no meio do lote.
 */
export const BULK_APPROVE_REASON_LABELS: Record<string, string> = {
  NOT_FOUND: 'Registro não encontrado.',
  FORBIDDEN: 'Você não tem acesso a este registro.',
  BULK_APPROVE_DISABLED: 'A política deste registro não permite aprovação em lote.',
  ACTIVITY_NOT_BULK_APPROVABLE: ACTIVITY_NOT_BULK_APPROVABLE_REASON,
  UNKNOWN_ERROR: 'Erro inesperado ao aprovar este registro.',
  [REQUIRES_OVERWRITE_CONFIRMATION]:
    'Outro registro aprovado já cobre um plantão deste registro. Abra o registro para confirmar a sobrescrita.'
}

export function bulkApproveReasonLabel(reason: string): string {
  if (!reason) return 'Motivo não informado.'
  return BULK_APPROVE_REASON_LABELS[reason] ?? reason
}

export interface BulkApproveReasonItem {
  containerId: number
  reason: string
}

export interface BulkApproveOutcomeRow {
  containerId: number
  reason: string
  label: string
  kind: 'failed' | 'skipped'
  /** Registro inexistente ou fora do escopo não tem o que abrir. */
  canOpen: boolean
}

const NOT_OPENABLE_REASONS = ['NOT_FOUND', 'FORBIDDEN']

export function buildBulkApproveOutcomeRows(result: {
  failed?: BulkApproveReasonItem[]
  skipped?: BulkApproveReasonItem[]
}): BulkApproveOutcomeRow[] {
  const toRow =
    (kind: BulkApproveOutcomeRow['kind']) =>
    (item: BulkApproveReasonItem): BulkApproveOutcomeRow => ({
      containerId: item.containerId,
      reason: item.reason,
      label: bulkApproveReasonLabel(item.reason),
      kind,
      canOpen: !NOT_OPENABLE_REASONS.includes(item.reason)
    })
  const rows = [
    ...(result.skipped ?? []).map(toRow('skipped')),
    ...(result.failed ?? []).map(toRow('failed'))
  ]
  // O que o auditor RESOLVE sozinho (confirmar a sobrescrita) vem primeiro.
  const actionable = (row: BulkApproveOutcomeRow) =>
    row.reason === REQUIRES_OVERWRITE_CONFIRMATION ? 0 : 1
  return rows.sort((a, b) => actionable(a) - actionable(b))
}

/**
 * `unidade · escala · data` do registro no modal de resultado. A data do plantão chega em
 * `YYYY-MM-DD`: vai em dd/mm/aaaa, como no resto da tela.
 */
export function buildBulkOutcomeSubtitle(item: {
  unit?: string | null
  schedule?: string | null
  date?: string | null
}): string {
  return [item.unit, item.schedule, toDDMMYYYY(item.date)].filter(Boolean).join(' · ')
}
